import type {
    Observer,
    Subject
} from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import BigQuery from '../../../common/BigQuery';
import GoogleAnalyticsEvents from '../../../common/GoogleAnalyticsEvents';
import SqueezelyEvents from '../../../common/SqueezelyEvents';
import AbstractFilter from '../../../common/search/store/AbstractFilter';
import ApiRequestState from '../../../common/search/store/ApiRequestState';
import SearchTrackingEvents from '../../../enums/searchTrackingEvents';
import AggregationFilter from '../store/AggregationFilter';
import HouseSearchApi from '../store/HouseSearchApi';
import HouseSearchFilters from '../store/HouseSearchFilters';
import HouseSearchListItems from '../store/HouseSearchListItems';
import PriceFilter from '../store/PriceFilter';
import SearchFormComponentFilterMapping from './SearchFormComponentFilterMapping';
import '../../../../css/components/_searchFormComponent.pcss';

export default class SearchFormComponent implements Observer {
    protected context: SearchFormComponentContextInterface;

    public get formData(): FormData {
        return new FormData(this.context.element);
    }

    #formFields: (HTMLInputElement | HTMLSelectElement)[] = [];

    readonly #filters = HouseSearchFilters.getInstance();

    readonly #apiRequestState = ApiRequestState.getInstance();

    readonly #resultsItems = HouseSearchListItems.getInstance();

    readonly #api = HouseSearchApi.getInstance();

    constructor(context: SearchFormComponentContextInterface) {
        this.context = context;
    }

    public onInit(): void {
        if (!(this.context.element instanceof HTMLFormElement)) {
            return;
        }

        this.#formFields = this.#getFormFields();
        this.#filters.forEach((filter: AbstractFilter) => filter.attach(this));

        this.setEventListeners();
        this.#trackUserData();
        this.#trackSearchFilterCloseDialog();
    }

    public update(subject: Subject | AbstractFilter): void {
        if (
            subject instanceof ApiRequestState &&
            subject.state === ApiRequestState.REQUEST_SUCCESS
        ) {
            this.#trackUserData();
        }

        if (!(subject instanceof AbstractFilter)) {
            return;
        }

        if (subject instanceof PriceFilter) {
            const minPrice = this.#formFields.find((element) => element.name === 'minPrice');
            const maxPrice = this.#formFields.find((element) => element.name === 'maxPrice');
            if (!minPrice || !maxPrice) return;

            minPrice.value = subject.parameters.minPrice || '0';
            minPrice.dispatchEvent(new Event('changed'));
            maxPrice.value = subject.parameters.maxPrice || '';
            return;
        }

        if (subject instanceof AggregationFilter) {
            const parameterName = subject.parameterName;
            const checkboxes = Array.from(
                this.context.element.querySelectorAll(`[name="${parameterName}"]`)
            ) as HTMLInputElement[];

            if (!checkboxes.length) return;

            const values = subject.selection;
            checkboxes.forEach((checkbox): void => {
                checkbox.checked = values.includes(checkbox.value);
            });
        }
    }

    protected setEventListeners(): void {
        this.#setFilterChangeEventHandlerOnFields();
        this.#apiRequestState.attach(this);

        this.context.element.addEventListener('submit', (event: Event) => {
            event.preventDefault();
            this.onSubmit();
        });
    }

    protected onSubmit(): void {
        this.#api.update();
    }

    #getFormFields(): (HTMLInputElement | HTMLSelectElement)[] {
        const priceRangeContainer: HTMLElement | null = this.context.element.querySelector(
            'section.search__pricerange'
        );

        if (priceRangeContainer === null) {
            return [];
        }

        const priceRangeFields: HTMLSelectElement[] = Array.from(
            priceRangeContainer?.querySelectorAll('select')
        );

        return priceRangeFields;
    }

    #trackUserData(): void {
        const dataLayerElement: HTMLElement | null = document.getElementById('data-layer');
        let initialData: StandardObjectInterface = {};

        if (dataLayerElement && dataLayerElement.hasAttribute('content')) {
            const dataLayerString: string | null = dataLayerElement.getAttribute('content');
            if (dataLayerString) {
                initialData = JSON.parse(dataLayerString);
            }
        }

        const listName = 'Search Results';
        SqueezelyEvents.trackSearchForm(this.#filters, initialData);
        GoogleAnalyticsEvents.trackSearchForm(
            listName,
            this.#resultsItems.houses,
            this.#resultsItems.total
        );
    }

    #trackSearchFilterCloseDialog(): void {
        const dialog = document.getElementById('modal_mobile_aggregations');

        if (!dialog) {
            return;
        }

        dialog.addEventListener('close', async () => {
            await GoogleAnalyticsEvents.trackSearchFilterCloseDialog(this.#resultsItems.total);
        });
    }

    #setFilterChangeEventHandlerOnFields(): void {
        const filterMapping = SearchFormComponentFilterMapping.mapFieldsToFilters();

        for (let index = 0; index < this.#formFields.length; index++) {
            const field: HTMLInputElement | HTMLSelectElement = this.#formFields[index];
            const name = !field.name && field.dataset.destinationField ? 'keyword' : field.name;
            const callback = filterMapping.get(name);

            if (field.dataset.onchange !== 'false' && callback !== undefined) {
                field.addEventListener('change', () => callback(field));
            }

            field.addEventListener('change', () => {
                if (field.dataset.onchange === 'false') {
                    return;
                }

                this.onSubmit();
                this.#trackPriceChangeEvent();
            });
        }
    }

    #trackPriceChangeEvent(): void {
        const minPrice = this.#formFields.find((element) => element.name === 'minPrice');
        const maxPrice = this.#formFields.find((element) => element.name === 'maxPrice');
        if (!minPrice || !maxPrice) return;

        BigQuery.trackWithDelay({
            eventName: SearchTrackingEvents.PRICE_RANGE,
            eventParams: [
                { eventKey: 'minPrice', eventValue: minPrice.value },
                { eventKey: 'maxPrice', eventValue: maxPrice.value }
            ]
        });
    }

    static getClassName(): string {
        return 'SearchFormComponent';
    }
}
